/**
 * The browser's own validation bubbles, in the page's language.
 *
 * `required`, `type="email"`, `minLength` and friends make the browser refuse a
 * submit and point at the field — which is what we want — but it words the
 * bubble in the *browser's* language, not the page's. A Romanian signup page in
 * an English Chrome said "Please fill out this field." under "Prenume".
 *
 * This keeps the native behaviour and only replaces the sentence: on each
 * `invalid` event it sets a translated message with `setCustomValidity`, and
 * clears it again as soon as the field is edited. Installed once, at the
 * document, so every form — present and future, the booking widget included —
 * gets it without a line of its own. A form that validates itself
 * (`noValidate`, like Withdrawal.jsx) never fires `invalid` and is untouched.
 *
 * Plain JS on purpose: it runs before React and has no JSX.
 */

/** Controls whose custom message is ours, so we never clear somebody else's. */
const localized = new WeakSet();

/** "2026-10-06" as the reader writes a date; anything else as it is. */
const formatLimit = (control, value, language) => {
  if (control.type !== "date" || !value) return value;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(language);
};

/**
 * The sentence for whatever is wrong with `control`, or null when nothing we
 * know about is (a component's own custom message, or a valid field).
 */
export const validationMessage = (control, t, language) => {
  const validity = control.validity;
  const type = (control.type || "").toLowerCase();

  if (validity.valueMissing) {
    if (type === "checkbox") return t("Validation.checkboxRequired");
    if (type === "radio") return t("Validation.radioRequired");
    if (type === "file") return t("Validation.fileRequired");
    if (control.tagName === "SELECT") return t("Validation.selectRequired");
    return t("Validation.required");
  }

  if (validity.typeMismatch) {
    if (type === "email") return t("Validation.email");
    if (type === "url") return t("Validation.url");
    return t("Validation.invalid");
  }

  if (validity.tooShort) {
    return t("Validation.tooShort", { count: control.minLength, current: control.value.length });
  }

  if (validity.tooLong) {
    return t("Validation.tooLong", { count: control.maxLength, current: control.value.length });
  }

  // A pattern says nothing a person can act on; its title is where the page
  // explains it, and the browser shows the title too.
  if (validity.patternMismatch) return control.title || t("Validation.pattern");

  if (validity.rangeUnderflow) {
    return t("Validation.rangeUnderflow", { min: formatLimit(control, control.min, language) });
  }

  if (validity.rangeOverflow) {
    return t("Validation.rangeOverflow", { max: formatLimit(control, control.max, language) });
  }

  if (validity.badInput && type === "number") return t("Validation.number");
  if (validity.badInput || validity.stepMismatch) return t("Validation.invalid");

  return null;
};

const clear = (control) => {
  if (!localized.has(control)) return;
  control.setCustomValidity("");
  localized.delete(control);
};

const clearForm = (form) => {
  if (form?.elements) Array.from(form.elements).forEach(clear);
};

/**
 * Wires the translation into `root` (the document, in the app). Returns the
 * function that removes it again, for tests.
 *
 * `i18n` is read at the moment a field fails, so a language switch applies to
 * the very next bubble.
 */
export const installLocalizedValidation = (i18n, root = document) => {
  const onInvalid = (event) => {
    const control = event.target;
    if (typeof control?.setCustomValidity !== "function") return;

    clear(control);
    if (control.validity.valid || control.validity.customError) return;

    const message = validationMessage(
      control,
      (key, options) => i18n.t(key, options),
      i18n.resolvedLanguage || i18n.language,
    );
    if (!message) return;

    control.setCustomValidity(message);
    localized.add(control);
  };

  // Typing, ticking or picking a value is the person fixing the field. A custom
  // message keeps a field invalid until it is cleared, so leaving it would
  // block a field that is now fine.
  const onEdit = (event) => clear(event.target);

  // A value React sets in code — an autofill from the registry lookup, a form
  // reset — fires no input event. So before the browser validates a form again
  // (a submit button's click runs first), drop our messages and let it look at
  // the fields afresh; whatever is still wrong fails again and is re-worded.
  const onClick = (event) => {
    const button = event.target?.closest?.("button, input[type='submit'], input[type='image']");
    if (button?.form && (button.type === "submit" || button.type === "image")) clearForm(button.form);
  };

  // Enter in a field submits its form without a click on some browsers.
  const onKeyDown = (event) => {
    if (event.key === "Enter") clearForm(event.target?.form);
  };

  root.addEventListener("invalid", onInvalid, true);
  root.addEventListener("input", onEdit, true);
  root.addEventListener("change", onEdit, true);
  root.addEventListener("click", onClick, true);
  root.addEventListener("keydown", onKeyDown, true);

  return () => {
    root.removeEventListener("invalid", onInvalid, true);
    root.removeEventListener("input", onEdit, true);
    root.removeEventListener("change", onEdit, true);
    root.removeEventListener("click", onClick, true);
    root.removeEventListener("keydown", onKeyDown, true);
  };
};
